import { asapScheduler, Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  observeOn,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeWhile,
} from 'rxjs/operators';

/**
 * @param criteria$ An observable of metadata that "resets" the scroll operation back to the start. This could include search terms, filters, sort order, etc.
 * @param callback A function that takes in the paging metadata, fetches the next page, and returns the new items and metadata.
 * @param trigger$ An observable used to trigger the next page of items to be fetched.
 * @returns An observable state containing the paged items and the paging metadata.
 *
 * @example
 *
 * // Examples of criteria
 * const searchText$ = new BehaviorSubject<string>('');
 * const critera$ = searchText$.pipe(distinctUntilChanged(), debounceTime(500));
 *
 * const filters$ = ...
 * const sortOrder$ = ...
 * const criteria$ = combineLatest([searchText$, filters$, sortOrder$]).pipe(debounceTime(500));
 *
 * // Example of callback
 * (criteria, cursor) => {
 *   const { searchText, filters, sortOrder } = criteria;
 *   return someApiCall(searchText, filters, sortOrder, cursor).pipe(
 *    map(response => ({
 *      // You must normalize your response fields to "items", "cursor", and "hasMore"
 *      items: response.someItems,
 *      cursor: response.cursor,
 *      hasMore: response.hasMore,
 *    })
 *  );
 * }
 *
 * // Example of trigger
 * const trigger$ = new Subject();
 */
export const scrollable = <ItemType, Criteria>(
  criteria$: Observable<Criteria>,
  callback: (
    criteria: Criteria,
    cursor: string,
  ) => Observable<{ items: ItemType[]; cursor: string; hasMore: boolean; totalResults?: number }>,
  trigger$: Observable<unknown>,
) => {
  return criteria$.pipe(
    observeOn(asapScheduler),
    switchMap((criteria: Criteria) => {
      let cursor: string = null;
      let hasMore = true;
      let items: ItemType[] = [];
      return trigger$.pipe(
        startWith(true),
        takeWhile(() => hasMore),
        switchMap(() => {
          return callback(criteria, cursor).pipe(
            take(1),
            map((result) => {
              cursor = result.cursor;
              hasMore = result.hasMore;
              if (result.items) {
                items = [...items, ...result.items];
              }
              return {
                items,
                loading: false,
                hasMore,
                empty: items.length === 0 && !hasMore,
                totalResults: result.totalResults || null,
                criteria,
              };
            }),
            startWith({ items, loading: true, hasMore: true, empty: false, totalResults: null, criteria }),
          );
        }),
      );
    }),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );
};

export class Scrollable<ItemType, Criteria> {
  readonly state$ = scrollable(this.criteria$, this.callback, this.trigger$);
  readonly items$ = this.state$.pipe(
    map((value) => value.items),
    distinctUntilChanged(),
  );
  readonly loading$ = this.state$.pipe(
    map((value) => value.loading),
    distinctUntilChanged(),
  );
  readonly hasMore$ = this.state$.pipe(
    map((value) => value.hasMore),
    distinctUntilChanged(),
  );
  readonly empty$ = this.state$.pipe(
    map((value) => value.empty),
    distinctUntilChanged(),
  );
  readonly totalResults$ = this.state$.pipe(map((value) => value.totalResults));

  /**
   * @param criteria$ An observable of metadata that "resets" the scroll operation back to the start. This could include search terms, filters, sort order, etc.
   * @param callback A function that takes in the paging metadata, fetches the next page, and returns the new items and metadata.
   * @param trigger$ A subject used to trigger the next page of items to be fetched.
   *
   * @see scrollable for more information on these parameters and examples of their use.
   */
  constructor(
    private readonly criteria$: Observable<Criteria>,
    private readonly callback: (
      criteria: Criteria,
      cursor: string,
    ) => Observable<{ items: ItemType[]; cursor: string; hasMore: boolean; totalResults?: number }>,
    private readonly trigger$ = new Subject<void>(),
  ) {}

  loadMore(): void {
    this.trigger$.next();
  }
}
